/* ===========================================================================
   NC quick add.

   A cart glyph on every product photograph in a grid; tapping it opens a small
   right-hand panel with the photograph, the name, the price, the variants and
   one black ADD TO CART. No size guide, no description, no link out — it is
   the shortest path from a grid to the cart drawer.

   HOW THE ADD ACTUALLY HAPPENS: the panel submits an ordinary
   <form action="/cart/add">. snippets/nc-add-to-cart.liquid already intercepts
   every such submit on the site, POSTs it, dispatches the theme's cart:update
   and opens the cart drawer. Nothing here talks to the cart directly — one
   code path for every add on the store, and this panel cannot drift from it.

   THE BUTTON IS INJECTED, NOT TEMPLATED. The collection grid is Horizon's own
   product-card / card-gallery, and those are large shared files that the
   theme's own quick-add settings also write into. Injecting from here keeps
   the change in one file and out of the way of a theme update.

   HORIZON'S OWN QUICK ADD (Theme settings → quick_add / mobile_quick_add) IS
   ON in this shop's settings, which put TWO controls on every card: Horizon's
   Add/Choose button and this glyph. nc-quick-add.css now hides Horizon's
   wherever ours is present, so the setting cannot double the control again.
   Turning the setting off is still the tidier state.

   THE PANEL OPENS INSTANTLY. It used to fetch /products/<handle>.js on tap and
   show "Loading" for the round trip. Now:
     1. every handle visible in a grid is PREFETCHED in the background once the
        page is idle (a few KB each, one request per product, cached);
     2. pointer-enter / touch-start on a glyph prefetches that one immediately,
        so on a phone the ~100ms between finger-down and click covers most of
        the request;
     3. if the data is already here, the panel is drawn synchronously in the
        same frame the click lands — no loading state at all.
   The stylesheet and this script are preloaded from the snippet so the FIRST
   open on a page is not waiting on either.

   The button sits INSIDE the card's <a>, which is why the click handler stops
   the event dead — without preventDefault the card navigates to the product
   page underneath the panel.
   =========================================================================== */
(function () {
  if (window.__ncQuickAdd) return;
  window.__ncQuickAdd = true;

  var root, panel, scrim, product, selection, moneyFormat;

  function money(cents) {
    var v = (cents / 100).toFixed(2);
    var fmt = moneyFormat || '${{amount}}';
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, v.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, v.replace('.', ','))
      .replace(/<[^>]*>/g, '');
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function iconFrom(id, fallback) {
    var tpl = document.getElementById(id);
    if (tpl && tpl.content && tpl.content.firstElementChild) {
      return tpl.content.firstElementChild.cloneNode(true);
    }
    var wrap = document.createElement('span');
    wrap.innerHTML = fallback;
    return wrap.firstElementChild;
  }

  /* ---- product data: cache + prefetch ----------------------------------- */

  var pending = {};   /* handle -> Promise */
  var ready = {};     /* handle -> product JSON, once resolved */

  function load(handle) {
    if (!handle) return Promise.resolve(null);
    if (!pending[handle]) {
      pending[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { if (p) ready[handle] = p; return p; })
        .catch(function () { delete pending[handle]; return null; });
    }
    return pending[handle];
  }

  var prefetchQueue = [];
  var prefetching = false;

  function drainPrefetch(deadline) {
    /* A few at a time, only while the browser is idle, so seventy cards do not
       become seventy simultaneous requests competing with the images. */
    var n = 0;
    while (prefetchQueue.length && n < 4 && (!deadline || deadline.timeRemaining() > 4)) {
      load(prefetchQueue.shift());
      n++;
    }
    if (prefetchQueue.length) {
      schedulePrefetch();
    } else {
      prefetching = false;
    }
  }

  function schedulePrefetch() {
    prefetching = true;
    if (window.requestIdleCallback) {
      requestIdleCallback(drainPrefetch, { timeout: 1500 });
    } else {
      setTimeout(function () { drainPrefetch(null); }, 120);
    }
  }

  function queuePrefetch(handle) {
    if (!handle || pending[handle] || prefetchQueue.indexOf(handle) !== -1) return;
    prefetchQueue.push(handle);
    if (!prefetching) schedulePrefetch();
  }

  /* ---- the trigger ------------------------------------------------------

     THE SCAN IS THE EXPENSIVE PART, SO IT IS RATIONED THREE WAYS. A collection
     page has seventy-odd cards, and this theme mutates the DOM on a timer in
     two places — nc-drawer-extras relocates the drawer's variant selects every
     400ms and cart-drawer.liquid re-checks its rows every 700ms. An unfiltered
     observer therefore re-scanned all seventy cards several times a second,
     forever, on a page that was doing nothing.

       1. Mutations that add no element, or that happen inside the cart drawer
          or this panel, are ignored outright — that is the timer traffic.
       2. Whatever survives is coalesced into ONE scan per animation frame.
       3. Boxes already dealt with are remembered in a WeakSet, so a scan that
          does run costs a walk over known nodes rather than a DOM query each.

     A box with no resolvable product handle is deliberately NOT marked: its
     markup may still be streaming in, and it should be retried.

     THE BOX IS CLAIMED BEFORE THE BUTTON IS APPENDED. Marking it afterwards
     left a window in which a second scan could see an unclaimed box and add a
     second glyph — the "doubled icon" on collection cards. The data attribute
     is the claim (it survives without WeakSet support too). */

  var handled = typeof WeakSet === 'function' ? new WeakSet() : null;
  var scanQueued = false;

  function handleOf(node) {
    var card = node.closest('product-card, .pcard, li, article');
    var a = card && card.querySelector('a[href*="/products/"]');
    if (!a) a = node.closest('a[href*="/products/"]');
    if (!a) return null;
    var href = a.getAttribute('href') || '';
    var part = href.split('/products/')[1];
    return part ? part.split('?')[0].split('/')[0] : null;
  }

  function inject() {
    scanQueued = false;
    var media = document.querySelectorAll('.card-gallery, .pcard__media');
    for (var i = 0; i < media.length; i++) {
      var box = media[i];
      if (handled && handled.has(box)) continue;
      if (box.hasAttribute('data-nc-qa-box')) { if (handled) handled.add(box); continue; }

      if (box.querySelector('.nc-qa-btn') || box.closest('.cart-drawer__dialog, .nc-qa')) {
        box.setAttribute('data-nc-qa-box', '');
        if (handled) handled.add(box);
        continue;
      }

      var handle = handleOf(box);
      if (!handle) continue;

      /* claim first, then build */
      box.setAttribute('data-nc-qa-box', '');
      if (handled) handled.add(box);

      var btn = el('button', 'nc-qa-btn');
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Quick add');
      btn.setAttribute('data-nc-qa', handle);
      btn.appendChild(iconFrom('nc-qa-icon-cart', '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5.25 8h13.5l-1.1 11.5a1 1 0 0 1-1 .9H7.35a1 1 0 0 1-1-.9L5.25 8z"/><path d="M9 8.5V6.75a3 3 0 0 1 6 0V8.5"/></svg>'));
      box.appendChild(btn);

      queuePrefetch(handle);
    }
  }

  function scheduleInject() {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(inject);
  }

  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest && e.target.closest('.nc-qa-btn');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      open(btn.getAttribute('data-nc-qa'));
    },
    true
  );

  /* Intent = fetch now. On touch this fires ~100ms before the click. */
  function warm(e) {
    var btn = e.target.closest && e.target.closest('.nc-qa-btn');
    if (btn) load(btn.getAttribute('data-nc-qa'));
  }
  document.addEventListener('pointerenter', warm, true);
  document.addEventListener('touchstart', warm, { capture: true, passive: true });
  document.addEventListener('focusin', warm, true);

  /* ---- option plumbing --------------------------------------------------- */

  /* /products/x.js gives option names as strings on older shops and objects on
     newer ones, and never a per-option value list — so the values are read off
     the variants, in the order they appear. */
  function optionSets(p) {
    var names = (p.options || []).map(function (o) {
      return typeof o === 'string' ? o : o.name;
    });
    var sets = names.map(function () { return []; });
    (p.variants || []).forEach(function (v) {
      (v.options || []).forEach(function (val, i) {
        if (sets[i] && sets[i].indexOf(val) === -1) sets[i].push(val);
      });
    });
    return names.map(function (name, i) { return { name: name, values: sets[i] || [] }; });
  }

  function variantFor(values) {
    return (product.variants || []).find(function (v) {
      return v.options.every(function (val, i) { return values[i] === val; });
    });
  }

  /* Is any available variant reachable if option i were set to val? Sizes that
     no colour can supply are struck through rather than hidden — hiding them
     makes a run of sizes look like the shop never carried them. */
  function reachable(i, val) {
    return (product.variants || []).some(function (v) {
      if (v.options[i] !== val) return false;
      if (v.available === false) return false;
      return v.options.every(function (o, j) {
        return j === i || selection[j] === undefined || selection[j] === o || j > i;
      });
    });
  }

  function firstImageFor(i, val) {
    var hit = (product.variants || []).find(function (v) {
      return v.options[i] === val && v.featured_image && v.featured_image.src;
    });
    return hit ? hit.featured_image.src : null;
  }

  /* ---- render ------------------------------------------------------------ */

  function thumbSrc(variant) {
    var src =
      (variant && variant.featured_image && variant.featured_image.src) ||
      (product.featured_image) ||
      (product.images && product.images[0]);
    return src ? src.replace(/(\.[a-z]+)(\?|$)/i, '_300x$1$2') : null;
  }

  function draw() {
    var variant = variantFor(selection);
    panel.textContent = '';

    /* head */
    var head = el('div', 'nc-qa__head');
    var thumb = el('span', 'nc-qa__thumb');
    var src = thumbSrc(variant);
    if (src) {
      var img = new Image();
      img.src = src;
      img.alt = '';
      img.decoding = 'async';
      thumb.appendChild(img);
    }
    head.appendChild(thumb);

    var meta = el('div');
    meta.appendChild(el('p', 'nc-qa__title', product.title));

    var prices = el('div', 'nc-qa__prices');
    var price = variant ? variant.price : product.price;
    var was = variant ? variant.compare_at_price : product.compare_at_price;
    if (was && was > price) prices.appendChild(el('s', 'nc-qa__was', money(was)));
    prices.appendChild(el('span', 'nc-qa__now', money(price)));
    if (was && was > price) {
      prices.appendChild(el('span', 'nc-qa__save', 'Save ' + money(was - price)));
    }
    meta.appendChild(prices);
    head.appendChild(meta);

    var close = el('button', 'nc-qa__close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.appendChild(iconFrom('nc-qa-icon-close', '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4.6 3.5 10 8.9l5.4-5.4 1.1 1.1L11.1 10l5.4 5.4-1.1 1.1L10 11.1l-5.4 5.4-1.1-1.1L8.9 10 3.5 4.6z"/></svg>'));
    close.addEventListener('click', hide);
    head.appendChild(close);
    panel.appendChild(head);

    /* options */
    var body = el('div', 'nc-qa__body');
    optionSets(product).forEach(function (opt, i) {
      if (opt.values.length < 2 && (product.variants || []).length < 2) return;

      var wrap = el('div', 'nc-qa__opt');
      var label = el('span', 'nc-qa__optlabel');
      label.textContent = opt.name + ': ';
      var chosen = el('em', null, selection[i] || '');
      label.appendChild(chosen);
      wrap.appendChild(label);

      var isColour = /colou?r/i.test(opt.name);
      var values = el('div', 'nc-qa__values');

      opt.values.forEach(function (val) {
        var swatchSrc = isColour ? firstImageFor(i, val) : null;
        var chip = el('button', swatchSrc ? 'nc-qa__swatch' : 'nc-qa__chip', swatchSrc ? null : val);
        chip.type = 'button';
        chip.setAttribute('aria-pressed', selection[i] === val ? 'true' : 'false');
        if (swatchSrc) {
          chip.setAttribute('aria-label', val);
          var si = new Image();
          si.src = swatchSrc.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2');
          si.alt = '';
          si.loading = 'lazy';
          chip.appendChild(si);
        }
        if (!reachable(i, val)) chip.setAttribute('data-sold', '');
        chip.addEventListener('click', function () {
          if (chip.hasAttribute('data-sold')) return;
          selection[i] = val;
          /* Keep the rest of the selection legal: fall back to the first
             available combination that honours this choice. */
          if (!variantFor(selection) || variantFor(selection).available === false) {
            var fix = (product.variants || []).find(function (v) {
              return v.options[i] === val && v.available !== false;
            });
            if (fix) selection = fix.options.slice();
          }
          draw();
        });
        values.appendChild(chip);
      });

      wrap.appendChild(values);
      body.appendChild(wrap);
    });
    panel.appendChild(body);

    /* foot — a real form, so the site's one add-to-cart bridge handles it */
    var foot = el('div', 'nc-qa__foot');
    var form = document.createElement('form');
    form.action = '/cart/add';
    form.method = 'post';
    var id = document.createElement('input');
    id.type = 'hidden';
    id.name = 'id';
    id.value = variant ? variant.id : '';
    var qty = document.createElement('input');
    qty.type = 'hidden';
    qty.name = 'quantity';
    qty.value = '1';
    var add = el('button', 'nc-qa__add', variant && variant.available !== false ? 'Add to cart' : 'Sold out');
    add.type = 'submit';
    if (!variant || variant.available === false) add.disabled = true;
    form.appendChild(id);
    form.appendChild(qty);
    form.appendChild(add);
    form.addEventListener('submit', function () { setTimeout(hide, 40); });
    foot.appendChild(form);
    panel.appendChild(foot);
  }

  /* ---- open / close ------------------------------------------------------ */

  var lastFocus = null;

  function show(p) {
    product = p;
    var first =
      (p.variants || []).find(function (v) { return v.available !== false; }) ||
      (p.variants || [])[0];
    selection = first ? first.options.slice() : [];
    draw();
  }

  function open(handle) {
    if (!handle || !root) return;
    lastFocus = document.activeElement;
    root.setAttribute('data-open', '');
    requestAnimationFrame(function () { root.setAttribute('data-shown', ''); });
    document.documentElement.style.overflow = 'hidden';

    /* Already prefetched: draw now, in this same frame. */
    if (ready[handle]) {
      show(ready[handle]);
      return;
    }

    panel.textContent = '';
    panel.appendChild(el('p', 'nc-qa__loading', 'Loading'));

    load(handle).then(function (p) {
      if (!p || !root.hasAttribute('data-open')) return;
      show(p);
    });
  }

  function hide() {
    if (!root) return;
    root.removeAttribute('data-shown');
    document.documentElement.style.overflow = '';
    setTimeout(function () { root.removeAttribute('data-open'); }, 260);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function boot() {
    root = document.querySelector('[data-nc-qa-root]');
    if (!root) return;
    panel = root.querySelector('.nc-qa__panel');
    scrim = root.querySelector('.nc-qa__scrim');
    moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    scrim.addEventListener('click', hide);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && root.hasAttribute('data-open')) hide();
    });

    inject();

    /* Grids re-render on filter, sort, paginate and section reload. Everything
       else the page does is ignored — see the note above inject(). */
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.closest && node.closest('.cart-drawer__dialog, .nc-qa')) continue;
          scheduleInject();
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
